import type {
  CompanyStatus,
  HubspotCompanyLinks,
} from "@/services/api/types";
import {
  type NavMenu,
  type UserRoles,
} from "./nav.constants";

const shouldHideNavItem = (
  userRoles: string[],
  roleGuard?: UserRoles,
  statusGuard?: CompanyStatus,
  companyStatus?: string,
): boolean => {
  return !!(
    (statusGuard && statusGuard !== companyStatus) ||
    (roleGuard && !userRoles.includes(roleGuard))
  );
};

const updateNavItems = (
  item: NavMenu,
  projectSummaryLink: string | null,
  userRoles: string[],
  companyStatus: string,
  links?: HubspotCompanyLinks,
  dynamicLinks?: Record<string, string | undefined>,
): NavMenu => {
  const updatedItem = { ...item };

  if (updatedItem.dynamicLink) {
    const resolvedUrl = dynamicLinks?.[updatedItem.dynamicLink];
    if (resolvedUrl) {
      updatedItem.to = resolvedUrl;
    } else {
      return { ...updatedItem, hidden: true };
    }
  }

  if (
    updatedItem.replacedByDynamicLink &&
    dynamicLinks?.[updatedItem.replacedByDynamicLink]
  ) {
    return { ...updatedItem, hidden: true };
  }

  if (updatedItem.hubspotLabel && links) {
    // Replace link if there's a matching hubspotLabel
    if (
      updatedItem.hubspotLabel &&
      links[updatedItem.hubspotLabel as keyof typeof links]
    ) {
      updatedItem.to = links[updatedItem.hubspotLabel as keyof typeof links];
    }
  } else if (updatedItem.hubspotLabel) {
    updatedItem.disabled = true;
  }

  // Set project summary link if applicable
  if (projectSummaryLink && updatedItem.title === "Project summary") {
    updatedItem.to = projectSummaryLink;
  }

  // Determine if the item should be hidden based on roleGuard or statusGuard
  if (
    shouldHideNavItem(
      userRoles,
      updatedItem.roleGuard,
      updatedItem.statusGuard,
      companyStatus,
    )
  ) {
    return { ...updatedItem, hidden: true };
  }

  return updatedItem;
};

export const generateHeaderNav = (
  navMenu: ReadonlyArray<NavMenu>,
  projectSummaryLink: string | null,
  userRoles: string[],
  companyStatus: string,
  links?: HubspotCompanyLinks,
  dynamicLinks?: Record<string, string | undefined>,
): NavMenu[] => {
  const newNavMenu = navMenu.reduce<NavMenu[]>((acc, item) => {
    const updatedItem = updateNavItems(
      item,
      projectSummaryLink,
      userRoles,
      companyStatus,
      links,
      dynamicLinks,
    );

    // Exclude hidden items immediately
    if (updatedItem.hidden) return acc;

    // // Process secondary menu items recursively
    const updatedSecondary = updatedItem.secondary
      ?.map((secItem) =>
        updateNavItems(
          secItem,
          projectSummaryLink,
          userRoles,
          companyStatus,
          links,
          dynamicLinks,
        ),
      )
      .filter((secItem) => !secItem.hidden) as NavMenu[];

    if (updatedSecondary?.length) {
      updatedItem.secondary = updatedSecondary;
    }

    acc.push(updatedItem);
    return acc;
  }, []);

  return newNavMenu;
};
